<template>
	<Page :theme="'gray'" animate>
		<template v-slot:content>
			<div class="top">
				<SafeAreaTop :theme="closeTheme" :closePosition="closePosition" @close="onClose"> </SafeAreaTop>
				<img :src="require(`@/assets/post/single-workout.png`)" alt="" />
			</div>

			<div id="form" class="container text-center">
				<div class="title" :class="productsTheme">GET EXCLUSIVE WORKOUTS RECOMMENDED FOR YOU</div>

				<div class="form-group" :class="productsTheme">
					<label for="" class="form-group--label">Enter Your email</label>
					<input
						type="email"
						v-model="data.email"
						@input="v$.$reset()"
						:class="{ error: v$.email.$error }"
						class="form-control"
						:placeholder="t('workbooks.enter_your_email')"
					/>

					<div class="form-group--note">We will send your workbook to this email</div>
				</div>
			</div>

			<div class="container description">
				It’s designed to help you incorporate physical activity into your daily routine. Exercising doesn't have to feel
				like a chore, right?
				<span class="description--price">Total today: {{ workbook.localized_price }}</span>
			</div>
		</template>

		<template v-slot:footer>
			<div class="container">
				<p-button
					v-scroll-to="{ el: '#form', offset: -20, onDone: onScrollDone }"
					label=""
					:loading="isLoading"
					cls="btn-primary btn-block workout-workbooks-btn"
				>
					ACTIVATE
				</p-button>
			</div>
		</template>
	</Page>
</template>

<script>
import SafeAreaTop from '@/components/SafeAreaTop.vue'
import Page from '@/components/Page'
import PButton from '@/components/Button'

import { computed, reactive } from '@vue/reactivity'
import { email, required } from '@vuelidate/validators'
import { inject, onMounted, watch } from '@vue/runtime-core'
import { useI18n } from 'vue-i18n'

import useVuelidate from '@vuelidate/core'
import useApi from '@/composition/api.js'
import useEnv from '@/composition/env.js'
import { useStore } from 'vuex'
// import { i18n } from './i18n'
import md5 from 'md5'
import useConfig from '@/composition/config.js'

const WORKOUT_WORKBOOK = 'workout'

export default {
	components: {
		Page,
		SafeAreaTop,
		PButton
	},

	setup() {
		const api = useApi()
		const store = useStore()
		const env = useEnv()
		const appboy = inject('appboy')
		const amplitude = inject('amplitude')
		const { t, locale } = useI18n()
		const config = useConfig()

		const language = computed(() => locale.value)

		const productsTheme = computed(() => config.get({ key: 'workbooks.theme', default_value: 'white' }))
		const closeTheme = computed(() => config.get({ key: 'workbooks.close_buttom.theme', default_value: 'light' }))
		const closePosition = computed(() => config.get({ key: 'workbooks.close_buttom.position', default_value: 'right' }))

		const workbook = computed(
			() =>
				store.getters['products/getData']([
					{
						id: 'ios_workout_workbook_sale',
						text: t('workbooks.workout_workbook'),
						subtitle: '[price]',
						type: WORKOUT_WORKBOOK
					}
				])[0]
		)

		watch(
			() => store.state.products.prices.length,
			() => {
				amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallShow'), {
					product_ids: [workbook.value.id],
					product_type: 'workbook',
					prices: [workbook.value.product_price],
					currency: workbook.value.currency
				})
			}
		)

		/** Loading */
		let isLoading = computed(() => store.state.isLoading)

		/** Validation */
		const data = reactive({
			email: null
		})
		const rules = {
			email: { required, email }
		}
		const v$ = useVuelidate(rules, data)

		/** Email */
		let hasEmail = computed(() => Boolean(store.state.user.email))

		const checkEmail = () => {
			if (hasEmail.value) {
				data.email = store.state.user.email
			}
		}

		watch(() => hasEmail.value, checkEmail)

		onMounted(() => {
			checkEmail()
		})

		let userId = computed(() => store.state.user.user_id)

		const close = (Status = 'Close', product = {}) => {
			amplitude.logEvent('Post Paywall - Close', {
				Layout: 'Workbooks',
				From: store.getters['getSource'],
				Step: 'Workbooks',
				Status
			})

			amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallDone'), {
				action: Status.toLowerCase(),
				product_type: 'workbook',
				...product
			})

			api.close()
		}
		const onClose = () => close()

		/** Purchase */
		const purchase = async () => {
			const { id: productId } = workbook.value

			api.purchase({
				productId,
				onFinish: async () => {
					const options = {
						external_id: userId.value,
						has_workout_workbook: true,
						has_nutrition_workbook: false
					}

					if (!hasEmail.value || !userId.value) {
						store.commit('user/setUserId', md5(data.email))
						options.external_id = userId.value
						options.email = data.email
						options.language = api.getLanguage()
					}

					await appboy.userTrack([options]).then((response) => response.json())

					await appboy.canvas(env('BRAZE_WORKBOOKS_CANVAS_ID'), userId.value)

					amplitude.logEvent('Paywall - Done', {
						Layout: 'Workbooks',
						From: store.getters['getSource'],
						Step: 'Workbooks',
						ProductId: productId,
						Status: 'Purchase'
					})

					amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallDone'), {
						action: 'purchase',
						product_type: 'workbook',
						product_id: workbook.value.id,
						price: Math.abs(workbook.value.product_price),
						currency: workbook.value.currency
					})
				},
				onError: (product, text) => {
					console.log(product, text)
				},
				onCancel: () => {},
				closeOnSuccess: true
			})
		}

		/** Scroll */
		const onScrollDone = async () => {
			if (await v$.value.email.$validate()) {
				purchase()
			}
		}

		return {
			workbook,
			t,
			purchase,
			v$,
			data,
			onScrollDone,
			isLoading,
			hasEmail,
			onClose,
			closeTheme,
			closePosition,
			language,
			productsTheme
		}
	}
}
</script>

<style lang="scss" scoped>
.top {
	height: 195px;
	background-color: #7d8bf7;
	position: relative;

	img {
		position: absolute;
		top: 24px;
		left: calc(50% - 133px);
		height: 230px;
	}
}

.title {
	font-size: 1.6rem;
	line-height: 1.3;
	font-weight: 900;
	text-align: center;
	text-transform: uppercase;
	margin-bottom: 2rem;
	margin-top: 6rem;

	&.primary {
		text-transform: none;
	}

	@media (min-width: 768px) {
		margin-top: 6.5rem;
	}

	@media (min-width: 991px) {
		margin-top: 7.5rem;
	}
}

.form-group {
	margin-bottom: 15px;

	&--label {
		text-align: center;
		margin-bottom: 0.5rem;
		display: block;
		font-weight: 500;
		text-transform: uppercase;
	}

	&--note {
		text-align: center;
		font-size: 0.8rem;
		margin-top: 0.5rem;
		padding-left: 0.5rem;
		font-weight: 300;
	}

	&.primary {
		text-align: center;

		.form-control {
			text-align: center;
		}
	}
}

.form-control {
	text-align: center;
	display: block;
	width: 100%;
	padding: 1rem 1.3rem;
	font-size: 1.1275rem;
	font-weight: 300;
	line-height: 1.3;
	color: #212529;
	background-color: #fff;
	background-clip: padding-box;
	border: 1px solid #d3d3d3;
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	border-radius: 0.6rem;
	transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;

	&:focus {
		color: #212529;
		background-color: #fff;
		border-color: #86b7fe;
		outline: 0;
	}

	&:disabled {
		border-color: #bfbfbf;
		color: #4a4a4a;
		background-color: transparent;
		opacity: 1;
	}

	&.error {
		border-color: red;
	}
}

.description {
	margin-top: 34px;
	color: #323653;
	text-align: center;
	font-weight: 300;
	font-size: 1.2rem;

	&--price {
		display: block;
		margin-top: 24px;
		font-weight: 500;
		font-size: 1.3rem;
	}
}

.workout-workbooks-btn {
	line-height: 1.8;
}

[lang='ru'],
[lang='it'] {
	.form-control {
		font-size: 1rem;
	}
}
</style>
